import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import Socket, { ConnectedUser } from '../../connection/socket';
import { ChatRoom, makeId } from '../../stores/reducers/chat';
import Avatar from '../general-components/avatar';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import { softDeleteChatForMeetingAndRoom } from '../../stores/actions/chat';
import { GreenroomState as GS } from '../../stores/types/types';
import Popover from '../general-components/popover';
import useProfile from '../../hooks/use-profile';
import Dialog from '../general-components/dialog';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';

interface ChatLinkProps {
	/* eslint-disable-next-line no-unused-vars */
	onClick?: (convo: ChatLinkProps) => void;
	user?: ConnectedUser;
	icon?: any;
	title?: string;
	badge?: string;
	message?: any;
	room?: ChatRoom;
	children?: JSX.Element;
	unseen?: number;
	className?: string;
	showParticipants?: boolean;
	inRoom?: boolean;
	socket: Socket | null;
}

function ChatLink(props: ChatLinkProps) {
	const {
		onClick,
		user: initUser,
		icon,
		title,
		badge,
		message,
		unseen,
		room,
		className,
		showParticipants = false,
		inRoom = false,
		socket,
	} = props;

	const token = useSelector((state: GS) => state.SessionReducer.token);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const host = useSelector((state: GS) => state.SessionReducer.host);
  const deleteButtonRef = useRef(undefined as any);
	const currentUser = useSelector((state: GS) => state.SessionReducer.user);

	const dispatch = useDispatch();
	const [open, setOpen] = useState<boolean>(false);
	const [activeClass, setActiveClass] = useState<string>('closed');

	const {getMaskFromEmail} = useProfile({});

	function resolveMask(nUser: ConnectedUser | undefined) {
		if (!nUser?.email) { return undefined }
		const mask = getMaskFromEmail(nUser.email) ?? {};
		return {...nUser, ...mask} as ConnectedUser;
	};

	const user = resolveMask(initUser);

	function getFirstName() {
		if (message.sender) {
			const name = resolveMask(message.sender)?.first_name ?? ''
			return name;
		}

		return message?.first_name ?? '';
	}

	function getLastName() {
		if (message.sender) {
			const name = resolveMask(message.sender)?.last_name ?? ''
			return name;
		}

		return message?.last_name ?? '';
	}

	function handleClick() {
		if (typeof onClick === 'function') {
			onClick(props);
		}

		if (!showParticipants) {
			return;
		}

		if (!open) {
			setOpen(true);
			setActiveClass('open');
		} else {
			setOpen(false);
			setActiveClass('closed');
		}
	}

	function handleTransition() {
		if (activeClass === 'closed') setOpen(false);
	}

	const handleOpenDeleteModal = () => {
		dispatch(ModalActions.setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.DELETE_CHAT_ROOM_CONFIRMATION,
			modalData: {
				socket,
				room
			},
		}));
	};

	const [name, setName] = useState('');
	const [messageText, setMessageText] = useState('');
	const [titleText, setTitleText] = useState('');
	const [showThreeDotMenu, setShowThreeDotMenu] = useState(false);

	useEffect(() => {
		if (user) {
			const firstName = user.first_name ? `${user.first_name} ` : '';
			const lastName = user.last_name ?? '';
			setTitleText(firstName || lastName ? `${firstName}${lastName}` : user.email);
			setName(firstName || lastName ? `${firstName}${lastName}` : user.email);

			if (message && message.message) setMessageText(message.message);
			else setMessageText('No Messages');
		} else {
			if (title) setTitleText(title);

			if (message) {
				if (getFirstName() || getLastName()) {
					setName(`${getFirstName()} ${getLastName()}`);
				} else if (message.title) {
					setName(message.title);
				}

				setMessageText(message.message);
			} else {
				setName('');
				setMessageText('');
			}
		}
	}, [user, message, title, getMaskFromEmail]);

	const unseenMessageCount = useMemo((): string => {
		if (!unseen) {
			return '0';
		}

		if (unseen > 9) {
			return '9+';
		}

		return unseen.toString();
	}, [unseen]);

	const noExpand = room?.name === 'Presenter chat'
		? props.children?.props?.children?.length < 2
		: (room?.participants?.length ?? 0) < 2;

	const showCaret = useMemo(
		(): boolean => inRoom && showParticipants && !!room && !noExpand,
		[inRoom, showParticipants, room, noExpand],
	);

	return (
		<>
			<button
				className={classNames(
					'chat-link-wrapper',
					'width-full',
					'position-relative',
					'background-light',
					'padding-medium',
					className,
					{ open, 'no-expand': inRoom && noExpand },
				)}
				onClick={handleClick}
			>
				<div className="chat-link-container width-full align-items-center justify-content-space-between">
					<div className='align-items-center overflow-hidden width-calc-full'>
						<div className="chat-icon margin-right-medium">
							{user ? (
								<Avatar size={'chat'} user={user} noBorder={true} />
							) : (
								<div className='icon-wrapper'>{icon}</div>
							)}
						</div>
						<div className='chat-title text-align-left width-calc-full'>
							<label className='text-white truncate-overflow name'>{titleText}</label>
							{messageText && <>
								<ReactMarkdown
									components={{
										p: (componentProps: any) => (
											<div className='truncate-overflow font-weight-normal message'>
												{!user && <span className='text-white'>{name}: </span>}
												{componentProps.children}
											</div>
										),
										a: (componentProps: any) => (
											<a
												href={componentProps.href}
												rel="noopener noreferrer"
												target="_blank"
											>
												{componentProps.children}
											</a>
										),
									}}
								>
									{messageText}
								</ReactMarkdown>
							</>}
						</div>
					</div>
					<div className={'chat-right align-items-center'}>
						{!!badge && (<span>{badge}</span>)}
						<span className={classNames('unseen', 'margin-left-small', { hidden: !unseen })}>
							{unseenMessageCount}
						</span>
						{inRoom && host && room?.name !== 'Brandlive Q&A' && (
							<div
								className="flex cursor-pointer"
								onClick={(e: any) => {
									// don't want to trigger the parent onClick
									e.stopPropagation();

									// show / hide submenu
									setShowThreeDotMenu(!showThreeDotMenu);
								}}
							>
								<Icon size={13} color={COLORS.TEXT_GRAY_300} name={ICONS.THREE_DOTS_HORIZONTAL}/>
							</div>
						)}
						{!inRoom && <Icon name={ICONS.ARROW_RIGHT} size={17} color={COLORS.WHITE}/>}
						{showCaret && <Icon
							name={open ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN}
							color={open ? COLORS.WHITE : COLORS.DEFAULT_GRAY}
							size={12}
						/>}
					</div>
				</div>
				{open && (
					<div onTransitionEnd={handleTransition} className={classNames('chat-link-dropdown', 'width-full', 'overflow-scroll-y', activeClass)}>
						{props.children}
					</div>
				)}
				<Popover
					open={showThreeDotMenu}
					close={() => setShowThreeDotMenu(false)}
					className={classNames(`chat-${showCaret ? 'room' : 'user'}-popover`)}
				>
					<div
						className="chat-link-menu"
					>
						<button
							className='padding-button-medium'
							onClick={async (event) => {
								event?.stopPropagation();

								setShowThreeDotMenu(false);
								dispatch(softDeleteChatForMeetingAndRoom(
									token ?? '',
									meeting?.meeting ?? 0,
									room?.id ?? 0,
									user?.gr_user ?? 0,
								));
								const ids = [currentUser?.email, user?.email].filter(Boolean) as string[];
								const chatId = ids.length === 2 ? makeId(ids) : null;
								if (Number.isNaN(room?.id) && !chatId) {
									console.error('No room or chat id to clear.')
									return;
								}
								socket?.sendMessage('reload-chat', {chatId, room: room?.id});
							}}
						>
							<Icon name={ICONS.STOP_SCREENSHARE} size={16} color={COLORS.WHITE}/>
							<span
								className="padding-left-small font-size-12 font-weight-heavy"
								style={{ width: '88px' }}
							>
								Clear Chat
							</span>
						</button>
						{room && room?.id !== 0 && <button
							ref={deleteButtonRef}
							className='padding-button-medium'
							onClick={handleOpenDeleteModal}
						>
							<Icon name={ICONS.TRASH} size={16} color={COLORS.RED}/>
							<span
								className="padding-left-small font-size-12 font-weight-heavy"
								style={{ width: '88px', color: 'rgb(255, 95, 127)' }}
							>
								Delete Room
							</span>
						</button>}
					</div>
				</Popover>
			</button>
		</>
	);
}

export default ChatLink;
